// src/pages/HomeView/components/AssetsTab.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { homeService, type PersonFile } from '../../../services/homeService';
import { authStorage } from '../../../lib/storage';
import { AssetCard } from '@/components/AssetCard/AssetCard';
import { ImagesGallery, type Attachment } from '@/components/ImagesGallery';
import { useInfoWindow } from '../../../contexts/InfoWindowContext';
import { readViewCache, writeViewCache } from '@/lib/viewCache';

const PAGE_SIZE = 50;
const CACHE_KEY = 'C2_Cache_Home_Assets';

/** Assets cached: enough for the first screen. */
const CACHE_LIMIT = 25;

/** Trims for storage and drops the unused Binary blob (UI renders FileUrl). */
function assetsForCache(files: PersonFile[]): PersonFile[]
{
  return files.slice(0, CACHE_LIMIT).map(file => ({
    ...file,
    Thumbnail: file.Thumbnail ? { ...file.Thumbnail, Binary: null } : null,
  }));
}

interface AssetsTabProps {
  viewAsPersonId?: string;
}

export const AssetsTab: React.FC<AssetsTabProps> = ({ viewAsPersonId: _viewAsPersonId }) =>
{
  // Read cache for initial state
  const cachedFiles = useMemo(() =>
  {
    const parsed = readViewCache<PersonFile[]>(CACHE_KEY);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    return null;
  }, []);

  const [files, setFiles] = useState<PersonFile[]>(cachedFiles || []);
  const [loading, setLoading] = useState(!cachedFiles);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noAssets, setNoAssets] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { openInfoWindow } = useInfoWindow();

  useEffect(() =>
  {
    loadAssets();
  }, []);

  const loadAssets = async () =>
  {
    try
    {
      setLoading(files.length > 0 ? false : true);

      const personId = parseInt(authStorage.getPersonId() || '0');
      if (personId === 0)
      {
        setNoAssets(true);
        setLoading(false);
        return;
      }

      const response = await homeService.getFilesByPersonId({
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        personId,
        personIdVisitor: personId,
      });

      // Filter only files that have a thumbnail
      const withThumbnails = response.filter(f => f.Thumbnail);

      if (withThumbnails.length === 0 && files.length === 0)
      {
        setNoAssets(true);
      }
      else
      {
        setFiles(withThumbnails);
        setNoAssets(false);
        setHasMore(response.length >= PAGE_SIZE);
        setPageNumber(1);

        // Save to cache
        writeViewCache(CACHE_KEY, assetsForCache(withThumbnails));
      }
    }
    catch (error)
    {
      console.error('Error loading assets:', error);
      if (files.length === 0) setNoAssets(true);
    }
    finally
    {
      setLoading(false);
    }
  };

  const loadMoreAssets = useCallback(async () =>
  {
    if (!hasMore || loadingMore) return;

    try
    {
      setLoadingMore(true);
      const nextPage = pageNumber + 1;
      const personId = parseInt(authStorage.getPersonId() || '0');

      const response = await homeService.getFilesByPersonId({
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
        personId,
        personIdVisitor: personId,
      });

      const withThumbnails = response.filter(f => f.Thumbnail);

      if (withThumbnails.length > 0)
      {
        setFiles(prev => [...prev, ...withThumbnails]);
        setPageNumber(nextPage);
        setHasMore(response.length >= PAGE_SIZE);
      }
      else
      {
        setHasMore(false);
      }
    }
    catch (error)
    {
      console.error('Error loading more assets:', error);
    }
    finally
    {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, pageNumber]);

  // Infinite scroll handler
  const handleScroll = useCallback(() =>
  {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 800)
    {
      loadMoreAssets();
    }
  }, [loadMoreAssets, loadingMore, hasMore]);

  const galleryAttachments = useMemo<Attachment[]>(
    () =>
      files.map(f => ({
        AttachmentId: f.AttachmentId,
        EmbeddedImageId: f.EmbeddedImageId ?? undefined,
        FileUrl: f.FileUrl,
        Thumbnail: { FileUrl: f.Thumbnail?.FileUrl || f.FileUrl },
        Description: f.Description,
        FileName: f.FileName,
        FileExtension: f.FileExtension,
        Favorite: f.Favorite,
      })),
    [files],
  );

  const handleThumbnailClick = useCallback(
    (file: PersonFile) =>
    {
      const idx = files.findIndex(
        f => f.AttachmentId === file.AttachmentId && f.EmbeddedImageId === file.EmbeddedImageId,
      );
      setGalleryIndex(idx >= 0 ? idx : 0);
      setGalleryOpen(true);
    },
    [files],
  );

  const handleGalleryClose = useCallback(() =>
  {
    setGalleryOpen(false);
  }, []);

  const handleMarkImage = useCallback(
    (attachmentId: number, embeddedImageId: number, isFavorite: boolean) =>
    {
      setFiles(prev =>
      {
        const updated = prev.map(f =>
        {
          const isMatch =
            (attachmentId > 0 && f.AttachmentId === attachmentId) ||
            (embeddedImageId > 0 && f.EmbeddedImageId === embeddedImageId);
          if (isMatch)
          {
            return { ...f, Favorite: isFavorite ? 1 : 0 };
          }
          return f;
        });
        writeViewCache(CACHE_KEY, assetsForCache(updated));
        return updated;
      });
    },
    [],
  );

  const handleTitleClick = useCallback(
    (conversationId: number) =>
    {
      openInfoWindow(conversationId);
    },
    [openInfoWindow],
  );

  // Loading skeleton
  if (loading && files.length === 0)
  {
    return (
      <div className="assets-tab-content h-full overflow-hidden p-4">
        <div className="project-files-items gallery-tab-items">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div
              key={i}
              className="project-files-item animate-pulse"
              style={{ overflow: 'hidden' }}
            >
              <div style={{ paddingBottom: '62.5%', backgroundColor: '#e5e7eb' }} />
              <div style={{ borderTop: '1px solid #ddd', padding: 4 }}>
                <div
                  style={{ height: 10, backgroundColor: '#e5e7eb', borderRadius: 2, width: '75%' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (noAssets && files.length === 0)
  {
    return (
      <div className="assets-tab-content h-full flex items-center justify-center">
        <div className="text-center py-16">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">You have no assets to display</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="user-attachments user-attachments-tab-view h-full overflow-auto"
      style={{ backgroundColor: '#ededed' }}
      onScroll={handleScroll}
    >
      <div className="project-files-items gallery-tab-items" style={{ paddingBottom: 30 }}>
        {files.map((file, index) => (
          <AssetCard
            key={`${file.AttachmentId || file.EmbeddedImageId}-${index}`}
            file={file}
            onClickThumbnail={handleThumbnailClick}
            onClickTitle={handleTitleClick}
          />
        ))}
      </div>

      {/* No more items message */}
      {!hasMore && files.length > 0 && (
        <div className="text-center py-4 no_more_message">
          <span className="text-gray-400 text-sm">No more assets.</span>
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5dade2] mx-auto" />
        </div>
      )}

      <ImagesGallery
        open={galleryOpen}
        attachments={galleryAttachments}
        index={galleryIndex}
        onClose={handleGalleryClose}
        onMarkImage={handleMarkImage}
      />
    </div>
  );
};
